import { useState } from "react";
import { useLocalSearchParams } from "expo-router";
import { Platform, Pressable, StyleSheet, TextInput, View } from "react-native";

import { CenteredCardBrand } from "@/components/CenteredCardBrand";
import { Button } from "@/design/Button";
import { Screen } from "@/design/primitives";
import { Muted, Text } from "@/design/Text";
import { fonts, palette, radius, space } from "@/design/tokens";
import {
  firebaseErrorMessage,
  submitPlayTesterEmail
} from "@/firebase/callables";
import { useAppRouter } from "@/lib/appRouter";

type SubmissionState = "idle" | "submitting" | "submitted";

export default function AndroidRoute() {
  const { preview } = useLocalSearchParams<{ preview?: string }>();
  const router = useAppRouter();
  const [purchaseEmail, setPurchaseEmail] = useState("");
  const [googleEmail, setGoogleEmail] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [focused, setFocused] = useState<"purchase" | "google" | null>(null);
  const [state, setState] = useState<SubmissionState>("idle");
  const [error, setError] = useState<string | null>(null);
  const submitted = state === "submitted" || preview === "submitted";
  const submittedEmail = sentTo || "you@example.com";

  const submit = async () => {
    if (state === "submitting") return;
    // Validate on press rather than disabling the button. The disabled
    // gradient desaturates into something that reads as a rendering fault,
    // and sign-in already keeps its Continue button lit on an empty field —
    // this should behave the same way.
    if (!purchaseEmail.trim() || !googleEmail.trim()) {
      setError("Please fill in both email addresses.");
      return;
    }
    setState("submitting");
    setError(null);
    try {
      await submitPlayTesterEmail({ purchaseEmail, googleEmail });
      setSentTo(googleEmail.trim());
      setState("submitted");
    } catch (nextError) {
      setState("idle");
      setError(firebaseErrorMessage(nextError));
    }
  };

  return (
    <Screen enter="rise">
      {/* Deliberately the login screen's card, not PublicInfoShell. That shell
          is a document layout for privacy/support — long prose in a wide card.
          This is a conversion page and the first thing many Android families
          ever see of the product, so it should read as a sibling of sign-in. */}
      <View style={styles.wrap}>
        <View style={styles.card}>
          <CenteredCardBrand />

          {submitted ? (
            <>
              <Text variant="display" style={styles.headline}>
                You're on the list.
              </Text>
              <Muted style={styles.copy}>
                We'll send your install link to{" "}
                <Text variant="bodyStrong" color={palette.text}>
                  {submittedEmail}
                </Text>{" "}
                — usually within a few hours.
              </Muted>
              <View style={styles.webOption}>
                <Pressable
                  accessibilityRole="link"
                  onPress={() => router.push("/recitals")}
                >
                  <Muted variant="small" style={styles.webLink}>
                    In the meantime, enjoy the show on the web.
                  </Muted>
                </Pressable>
              </View>
            </>
          ) : (
            <>
              <Text variant="display" style={styles.headline}>
                Get it on Android.
              </Text>
              <Muted style={[styles.copy, styles.introCopy]}>
                Google adds one extra step for brand-new apps. Tell us who you
                are and we'll send your install link — usually within a few
                hours.
              </Muted>

              <View style={styles.field}>
                <Text variant="smallStrong">Email you bought with</Text>
                <TextInput
                  accessibilityLabel="Email you bought with"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect={false}
                  inputMode="email"
                  onBlur={() => setFocused(null)}
                  onChangeText={setPurchaseEmail}
                  onFocus={() => setFocused("purchase")}
                  placeholder="you@example.com"
                  placeholderTextColor={palette.faint}
                  returnKeyType="next"
                  spellCheck={false}
                  style={[styles.input, focused === "purchase" && styles.inputLit]}
                  textContentType="emailAddress"
                  value={purchaseEmail}
                />
              </View>

              <View style={styles.field}>
                <Text variant="smallStrong">Google Play Store email</Text>
                {/* One line, not three. The warning has to be unmissable but a
                    paragraph of amber reads as an error state and makes the
                    whole card feel like something went wrong. */}
                {/* <Text variant="small" color={palette.warning}>
                  Must match the email tied to the Play Store on your device.
                </Text> */}
                <TextInput
                  accessibilityLabel="Google Play Store email"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect={false}
                  inputMode="email"
                  onBlur={() => setFocused(null)}
                  onChangeText={setGoogleEmail}
                  onFocus={() => setFocused("google")}
                  onSubmitEditing={() => void submit()}
                  placeholder="you@example.com"
                  placeholderTextColor={palette.faint}
                  returnKeyType="send"
                  spellCheck={false}
                  style={[styles.input, focused === "google" && styles.inputLit]}
                  textContentType="emailAddress"
                  value={googleEmail}
                />
              </View>

              {error ? (
                <Text
                  accessibilityLiveRegion="polite"
                  accessibilityRole="alert"
                  color={palette.danger}
                  style={styles.copy}
                  variant="small"
                >
                  {error}
                </Text>
              ) : null}

              <Button
                label="Send my install link"
                loading={state === "submitting"}
                onPress={submit}
                size="lg"
              />
            </>
          )}

          {!submitted ? (
            <View style={styles.footnote}>
              <Muted variant="small" style={styles.copy}>
                On an iPhone or iPad? Recital Master is on the App Store.
              </Muted>
            </View>
          ) : null}
        </View>
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: palette.surface,
    borderColor: palette.hairline,
    borderRadius: radius.xl,
    borderWidth: 1,
    gap: space.lg,
    maxWidth: 460,
    padding: space.xxl,
    width: "100%"
  },
  copy: {
    textAlign: "center"
  },
  field: {
    gap: space.xs
  },
  footnote: {
    borderTopColor: palette.hairline,
    borderTopWidth: 1,
    paddingTop: space.lg
  },
  headline: {
    textAlign: "center"
  },
  introCopy: {
    marginBottom: space.lg
  },
  input: {
    backgroundColor: palette.surfaceSunken,
    borderColor: palette.hairline,
    borderRadius: radius.md,
    borderWidth: 1,
    color: palette.text,
    fontFamily: fonts.body,
    fontSize: 16,
    marginTop: space.xs,
    paddingHorizontal: space.lg,
    paddingVertical: 14,
    ...Platform.select({ web: { outlineStyle: "none" }, default: {} })
  } as never,
  inputLit: {
    borderColor: palette.hairlineStrong
  },
  webLink: {
    textAlign: "center",
    textDecorationLine: "underline"
  },
  webOption: {
    marginTop: space.xs,
  },
  wrap: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
    minHeight: 560,
    paddingVertical: space.xxxl
  },
});
